<template>
  <div>
    <div class="form-box">
      <div class="login__form form-box__form">
        <h4 class="form-box__title">Sign in as a professional</h4>

        <div class="form__field">
          <label class="form__field__label">Email</label>

          <input
            ref="emailInput"
            required
            v-model="form.email"
            type="email"
            name="email"
            autocomplete="username"
            placeholder="Your email"
            class="form__field__input"
            @blur="formFieldValidation('email', form.email)"
          />

          <FormFieldErrors v-if="errors.email" :errors="errors.email" />
        </div>

        <div class="form__field">
          <label class="form__field__label">Password</label>

          <input
            ref="passwordInput"
            v-model="form.password"
            required
            type="password"
            name="password"
            autocomplete="current-password"
            class="form__field__input"
            placeholder="Your password"
          />

          <FormFieldErrors v-if="errors.password" :errors="errors.password" />
        </div>

        <div class="login__form__forgot-password">
          <router-link :to="FORGOT_PASSWORD_ROUTE.path" class="link">
            Forgot password?
          </router-link>
        </div>

        <div id="sign-in-captcha-container">
          <Captcha
            ref="recaptcha"
            class="mt-4"
            @verify="onCaptchaVerify"
            @expired="onCaptchaExpired"
            @error="onCaptchaExpired"
          />
        </div>

        <button
          type="submit"
          class="form__button form__button--blue form-box__form__button login__form__button"
          @click="handleSubmit"
          :disabled="isSubmitting"
        >
          <span
            v-if="isSubmitting"
            class="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          ></span>

          <span v-else>Sign In</span>
        </button>

        <div class="divider">or</div>

        <div>
          <!--
            SSO shares the same captcha widget as the form above (see
            sign-in-captcha-container) rather than showing a second challenge:
            clicking through before it's solved just points the user at it.
          -->
          <button
            type="button"
            class="form__button form__button--blue-outline form-box__form__button"
            @click="handleGoogleLogin"
          >
            <img
              class="image-with-text__image"
              src="../assets/images/icons/icon-google.svg"
              alt="image"
            />
            Continue with Google
          </button>
        </div>

        <div class="login__form__signup-prompt">
          Don&apos;t have an account?
          <button
            type="button"
            class="login__form__signup-prompt__link"
            @click="handleSignup"
          >
            Sign Up
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import FormFieldErrors from "@/components/FormFieldErrors";
import Captcha from "@/components/Captcha";
import {
  loginByEmailAndPassword,
  formFieldValidationHelper,
  isFormValidHelper,
} from "@/helpers";
import { GOOGLE_REDIRECT_URL, ROUTES } from "@/const";
import { loginFormValidation } from "@/const/loginFromValidation";

const USER_NOT_FOUND_LINE_1 = "Provided password and email do not match";
const USER_NOT_FOUND_LINE_2 =
  "Try to login again - check the spelling of your email and password. If you cannot access your account try resetting your password.";
const CONFIRM_EMAIL_FIRST_ERROR =
  "You have to confirm your profile first. Please check out your email.";
const NOT_CONFIRMED_PART = "has not yet confirmed";
const SOMETHING_WENT_WRONG = "Something went wrong";

const FORGOT_PASSWORD_ROUTE = ROUTES.get("FORGOT_PASSWORD");
const SIGN_UP_ROUTE = ROUTES.get("SIGN_UP");

const props = defineProps({
  onSubmitErrors: Function,
});

const router = useRouter();
const route = useRoute();

const recaptcha = ref();
const emailInput = ref();
const passwordInput = ref();
const isCaptchaValid = ref(false);
const captchaToken = ref(null);
const form = ref({
  email: null,
  password: null,
});
const errors = ref({});
const submitErrors = ref([]);
const redirect = ref(route.query.redirect);
const googleRedirect = GOOGLE_REDIRECT_URL;
const isSubmitting = ref(false);

const handleSignup = () => {
  router.push(SIGN_UP_ROUTE.path);
};

const handleGoogleLogin = () => {
  if (!isCaptchaValid.value) {
    onSubmitErrors.value(["Please complete the captcha before continuing."]);
    document
      .getElementById("sign-in-captcha-container")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
    return;
  }
  redirectToGoogle();
};

// Hand off to the backend OAuth entry point with the verified captcha token so
// the BE can reject automated sign-ups/registrations (see BACKEND-CAPTCHA-SSO.md).
const redirectToGoogle = () => {
  const url = new URL(googleRedirect);
  url.searchParams.set("captcha", captchaToken.value);
  if (redirect.value) {
    url.searchParams.set("redirect", redirect.value);
  }
  window.location.href = url.toString();
};

const formFieldValidation = (fieldName, fieldValue) => {
  formFieldValidationHelper(
    form.value,
    loginFormValidation,
    fieldName,
    fieldValue,
    errors.value,
  );
  errors.value = { ...errors.value };
};

// Browser/password-manager autofill doesn't always fire the `input` event
// v-model relies on, so form.value can go stale even though the fields are
// visibly filled in. The DOM value is always the source of truth for what's
// on screen, so re-read it directly before validating/submitting.
const syncAutofilledValues = () => {
  if (emailInput.value) {
    form.value.email = emailInput.value.value || null;
  }
  if (passwordInput.value) {
    form.value.password = passwordInput.value.value || null;
  }
};

const handleSubmit = () => {
  syncAutofilledValues();

  Object.entries(form.value).forEach(([fieldName, fieldValue]) => {
    formFieldValidation(fieldName, fieldValue);
  });

  if (!isFormValidHelper(errors.value)) {
    return;
  }

  if (!isCaptchaValid.value) {
    onSubmitErrors.value(["Please complete the captcha before continuing."]);
    return;
  }

  sendUserData();
};

const onCaptchaVerify = (token) => {
  isCaptchaValid.value = true;
  captchaToken.value = token;
};

const onSubmitErrors = computed(() => props.onSubmitErrors);

const sendUserData = async () => {
  submitErrors.value = [];
  try {
    isSubmitting.value = true;
    await loginByEmailAndPassword(
      form.value.email,
      form.value.password,
      redirect.value,
    );
  } catch (err) {
    isSubmitting.value = false;
    let errorMessage = SOMETHING_WENT_WRONG;
    if (typeof err === "string") {
      errorMessage = "USER_NOT_FOUND";
    }
    if (err?.message) {
      errorMessage = err.message.includes(NOT_CONFIRMED_PART)
        ? CONFIRM_EMAIL_FIRST_ERROR
        : "USER_NOT_FOUND";
    }
    if (errorMessage === "USER_NOT_FOUND") {
      submitErrors.value.push(USER_NOT_FOUND_LINE_1);
      submitErrors.value.push(USER_NOT_FOUND_LINE_2);
    } else {
      submitErrors.value.push(errorMessage);
    }
    onSubmitErrors.value(submitErrors.value);
    isCaptchaValid.value = false;
    captchaToken.value = null;
    recaptcha.value.reset();
  }
};

const onCaptchaExpired = () => {
  isCaptchaValid.value = false;
  captchaToken.value = null;
  recaptcha.value.reset();
};
</script>
